"use client";
import InitialsComponent from "@/components/dashboard/general/svgs/InitialsComponent";
import IconComponent from "@/components/icon/IconComponent";
import LogoComponent from "@/components/logo/LogoComponent";
import VillageNotificationBell from "@/components/notifications/VillageNotificationBell";
import { classNames } from "@/helpers/common/classNames";
import { formatNumber } from "@/helpers/common/formatNumber";
import { toggleFullScreen } from "@/helpers/common/toggleFullScreen";
import { useAuth } from "@/hooks/auth/useAuth";
import { gql, useQuery } from "@apollo/client";
import { Menu, Transition } from "@headlessui/react";
import { ViewfinderCircleIcon } from "@heroicons/react/24/outline";
import { Search } from "lucide-react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const SEARCH_PLAYERS_QUERY = gql`
    query SearchPlayers($searchQuery: String, $limit: Int) {
        searchPlayers(searchQuery: $searchQuery, limit: $limit) {
            id
            name
            nickname
        }
    }
`;

const TOP_NAV_RP_QUERY = gql`
    query TopNavRpQuery {
        playerStats {
            ravenguardPoints
        }
        vanderwoodStreamStatus {
            isLive
        }
        featureFlags {
            streamTeamChannelPointRewardEnabled
        }
    }
`;

interface Player {
    id: string;
    name: string;
    nickname: string;
}

const TopNav: React.FC = () => {
    const { user, logoutUser } = useAuth();
    const navigate = useNavigate();
    const [playerSearch, setPlayerSearch] = useState("");
    const [showResults, setShowResults] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    const userName = user?.twitchUsername || "John Doe";
    const myAccountLink = `/dashboard/my-account`;
    const avatarUrl = user?.avatarUrl || null;
    const player = user?.player || null;
    const clan = player?.clan || null;
    const clanColor = clan?.color?.hex || "#000000";
    const moderator = player?.moderator || false;
    const gota = player?.isGota || false;
    const vip = player?.vip || false;
    const vanderbucks = player?.bux?.balance || 0;
    const { data: rpData } = useQuery(TOP_NAV_RP_QUERY, {
        pollInterval: 30000,
        fetchPolicy: "cache-and-network",
    });
    const ravenguardPoints = rpData?.playerStats?.ravenguardPoints || 0;
    const isVanderwoodLive = Boolean(rpData?.vanderwoodStreamStatus?.isLive);
    const isStreamTeamRewardFeatureEnabled = Boolean(
        rpData?.featureFlags?.streamTeamChannelPointRewardEnabled,
    );
    const goldenWohToken = player?.goldenWohToken || false;
    const isStaff = user?.isStaff || false;

    const { data: searchData, loading: searchLoading } = useQuery<{
        searchPlayers: Player[];
    }>(SEARCH_PLAYERS_QUERY, {
        variables: { searchQuery: playerSearch, limit: 10 },
        skip: playerSearch.length < 2,
    });

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target as Node)
            ) {
                setShowResults(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handlePlayerClick = (playerId: string) => {
        navigate(`/dashboard/players/${playerId}`);
        setPlayerSearch("");
        setShowResults(false);
    };

    return (
        <>
            <div className="border-b-brand bg-brand_charcoal sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b-2 pr-4 shadow-xs sm:gap-x-6 sm:pr-6 lg:pr-8">
                {/* These should be aligned to the right. */}
                <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                    {/* Make link take height of parent div */}
                    <Link to="/dashboard" className="h-full items-center p-4">
                        <LogoComponent className="h-full" />
                    </Link>

                    {/* Mobile Search Toggle Button */}
                    <button
                        onClick={() => setShowMobileSearch(!showMobileSearch)}
                        className="text-brand hover:text-brand/80 -m-2.5 p-2.5 lg:hidden"
                        aria-label="Toggle search"
                    >
                        <Search className="h-5 w-5" />
                    </button>

                    {/* Desktop Player Search - Hidden on mobile */}
                    <div
                        className="relative hidden items-center lg:flex"
                        ref={searchRef}
                    >
                        <div className="relative w-64">
                            <Search className="text-brand absolute top-2.5 left-3 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search Villagers..."
                                value={playerSearch}
                                onChange={(e) => {
                                    setPlayerSearch(e.target.value);
                                    setShowResults(true);
                                }}
                                onFocus={() => setShowResults(true)}
                                className="bg-brand_charcoal border-brand text-brand placeholder-brand/60 focus:border-brand focus:ring-brand w-full rounded-lg border py-2 pr-4 pl-9 text-sm focus:ring-1 focus:outline-none"
                            />
                        </div>

                        {/* Search Results Dropdown */}
                        {showResults && playerSearch.length >= 2 && (
                            <div className="bg-brand_charcoal border-brand absolute top-full left-0 z-50 mt-1 w-64 rounded-lg border shadow-lg">
                                {searchLoading && (
                                    <div className="text-brand p-4 text-center text-sm">
                                        Searching...
                                    </div>
                                )}
                                {!searchLoading &&
                                    searchData?.searchPlayers.length === 0 && (
                                        <div className="text-brand p-4 text-center text-sm">
                                            No players found
                                        </div>
                                    )}
                                {!searchLoading &&
                                    searchData?.searchPlayers.map((player) => (
                                        <button
                                            key={player.id}
                                            onClick={() =>
                                                handlePlayerClick(player.id)
                                            }
                                            className="border-brand hover:bg-brand/10 w-full border-b p-3 text-left text-sm last:border-b-0"
                                        >
                                            <span className="text-brand font-semibold">
                                                {player.name}
                                            </span>
                                            {player.nickname && (
                                                <span className="text-brand/60 ml-2">
                                                    ({player.nickname})
                                                </span>
                                            )}
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>

                    <div className="ml-auto flex items-center gap-x-4 lg:gap-x-6">
                        <div
                            className={`hidden items-center gap-2 rounded-xl px-2 py-1 text-xs leading-4 font-semibold lg:flex ${
                                isVanderwoodLive
                                    ? "bg-red-600 text-white"
                                    : "bg-zinc-700 text-zinc-200"
                            }`}
                        >
                            <span
                                className={`h-2 w-2 rounded-full ${
                                    isVanderwoodLive
                                        ? "animate-pulse bg-red-300"
                                        : "bg-zinc-400"
                                }`}
                            />
                            {isVanderwoodLive ? "LIVE" : "OFFLINE"}
                        </div>

                        {/* Separator */}
                        <div
                            className="bg-brand hidden lg:block lg:h-full lg:w-px"
                            aria-hidden="true"
                        />

                        {gota && (
                            <div
                                className={`text-gota bg-gotabone hidden items-center rounded-xl px-2 py-1 text-xs leading-4 font-semibold lg:flex`}
                            >
                                GOTA
                            </div>
                        )}

                        {/* We show a pill saying "Moderator" if the user is a moderator */}
                        {/* It will use the twitch moderator green color. */}

                        {moderator && (
                            <div
                                className={`hidden items-center rounded-xl px-2 py-1 text-xs leading-4 font-semibold lg:flex`}
                                style={{ backgroundColor: "#00FF00" }}
                            >
                                MOD
                            </div>
                        )}

                        {vip && (
                            <div
                                className={`hidden items-center rounded-xl px-2 py-1 text-xs leading-4 font-semibold lg:flex`}
                                style={{ backgroundColor: "#E005B9" }}
                            >
                                VIP
                            </div>
                        )}

                        {/* We show a pill saying "VIP" if the user is a VIP */}
                        {/* It will use the twitch VIP purple color. */}

                        {/* We show a pill with with clan tag and bg as the clan color */}
                        {clan && (
                            <div
                                className={`hidden items-center rounded-xl px-2 py-1 text-xs leading-4 font-semibold lg:flex`}
                                style={{ backgroundColor: clanColor }}
                            >
                                {clan.tag}
                            </div>
                        )}

                        {goldenWohToken && (
                            <>
                                {/* Seperator */}
                                <div
                                    className="bg-brand hidden lg:block lg:h-full lg:w-px"
                                    aria-hidden="true"
                                />

                                <div className="flex items-center gap-x-2 lg:gap-x-4">
                                    <div className="h-10">
                                        {/* adjust width/height as needed */}
                                        <img
                                            src="/images/golden_woh_token.png"
                                            alt="Golden WOH Token"
                                            className="h-full w-full object-contain"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Seperator */}
                        <div
                            className="bg-brand hidden lg:block lg:h-full lg:w-px"
                            aria-hidden="true"
                        />

                        <div className="flex items-center gap-x-2">
                            <div className="h-8">
                                {/* adjust width/height as needed */}
                                <img
                                    src="/images/vanderbucks.png"
                                    alt="Vanderbucks"
                                    className="h-full w-full object-contain"
                                />
                            </div>
                            <div className="text-stats text-lg font-semibold">
                                <span>{formatNumber(vanderbucks)}</span>
                            </div>
                        </div>

                        {isStreamTeamRewardFeatureEnabled && (
                            <>
                                {/* Seperator */}
                                <div
                                    className="bg-brand hidden lg:block lg:h-full lg:w-px"
                                    aria-hidden="true"
                                />

                                <div className="flex items-center gap-x-2">
                                    <div className="h-8">
                                        <img
                                            src="/images/rp-icon.png"
                                            alt="Ravenguard Points"
                                            className="h-full w-full object-contain"
                                        />
                                    </div>
                                    <div className="text-stats text-lg font-semibold">
                                        <span>
                                            {formatNumber(ravenguardPoints)}
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Seperator */}
                        <div
                            className="bg-brand hidden lg:block lg:h-full lg:w-px"
                            aria-hidden="true"
                        />

                        <VillageNotificationBell />

                        <button
                            type="button"
                            onClick={toggleFullScreen}
                            className="text-brand hover:text-brand -m-2.5 hidden p-2.5 md:block"
                        >
                            <span className="sr-only">Full Screen</span>
                            <ViewfinderCircleIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                            />
                        </button>

                        {/* Separator */}
                        <div
                            className="bg-brand hidden lg:block lg:h-full lg:w-px"
                            aria-hidden="true"
                        />

                        {/* Profile dropdown */}
                        <Menu as="div" className="relative">
                            <Menu.Button className="-m-1.5 flex items-center p-1.5">
                                <span className="sr-only">Open user menu</span>
                                {avatarUrl ? (
                                    <img
                                        className="border-brand h-8 w-8 rounded-full border-2"
                                        src={avatarUrl}
                                        alt=""
                                    />
                                ) : (
                                    <InitialsComponent name={userName} />
                                )}
                                <span className="hidden lg:flex lg:items-center">
                                    <span
                                        className="text-brand ml-4 text-sm leading-6 font-semibold"
                                        aria-hidden="true"
                                    >
                                        {userName}
                                    </span>
                                    <IconComponent
                                        iconName="chevron-down-solid-20"
                                        className="text-brand ml-2 h-5 w-5"
                                        aria-hidden={true}
                                    />
                                </span>
                            </Menu.Button>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="ring-brand bg-brand_charcoal absolute -right-3.5 z-10 mt-2.5 w-44 origin-top-right rounded-md py-2 shadow-lg ring-1 focus:outline-hidden">
                                    {isStaff && (
                                        <Menu.Item>
                                            <Link
                                                to="/longhouse"
                                                className={classNames(
                                                    "text-brand block px-3 py-1 text-sm leading-6",
                                                )}
                                            >
                                                Longhouse
                                            </Link>
                                        </Menu.Item>
                                    )}
                                    <Menu.Item>
                                        <Link
                                            to="/dashboard/inbox"
                                            className={classNames(
                                                "text-brand block px-3 py-1 text-sm leading-6",
                                            )}
                                        >
                                            Inbox
                                        </Link>
                                    </Menu.Item>
                                    <Menu.Item>
                                        <Link
                                            to="/dashboard/surveys"
                                            className={classNames(
                                                "text-brand block px-3 py-1 text-sm leading-6",
                                            )}
                                        >
                                            Surveys
                                        </Link>
                                    </Menu.Item>
                                    <Menu.Item>
                                        <Link
                                            to={myAccountLink}
                                            className={classNames(
                                                "text-brand block px-3 py-1 text-sm leading-6",
                                            )}
                                        >
                                            Account
                                        </Link>
                                    </Menu.Item>
                                    <Menu.Item>
                                        <Link
                                            to="#"
                                            onClick={logoutUser}
                                            className={classNames(
                                                "text-brand block px-3 py-1 text-sm leading-6",
                                            )}
                                        >
                                            Sign Out
                                        </Link>
                                    </Menu.Item>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    </div>
                </div>
            </div>

            {/* Mobile Search Overlay */}
            {showMobileSearch && (
                <div className="bg-brand_charcoal fixed inset-0 z-50 flex flex-col p-4 lg:hidden">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-brand text-xl font-semibold">
                            Search Villagers
                        </h2>
                        <button
                            onClick={() => setShowMobileSearch(false)}
                            className="text-brand hover:text-brand/80 -m-2 p-2"
                            aria-label="Close search"
                        >
                            <svg
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="relative">
                        <Search className="text-brand absolute top-3 left-3 h-5 w-5" />
                        <input
                            type="text"
                            placeholder="Search Villagers..."
                            value={playerSearch}
                            onChange={(e) => {
                                setPlayerSearch(e.target.value);
                            }}
                            autoFocus
                            className="bg-brand_charcoal border-brand text-brand placeholder-brand/60 focus:border-brand focus:ring-brand w-full rounded-lg border py-3 pr-4 pl-11 text-base focus:ring-1 focus:outline-none"
                        />
                    </div>

                    {/* Mobile Search Results */}
                    <div className="mt-4 flex-1 overflow-y-auto">
                        {playerSearch.length < 2 && (
                            <div className="text-brand/60 text-center text-sm">
                                Type at least 2 characters to search
                            </div>
                        )}
                        {playerSearch.length >= 2 && searchLoading && (
                            <div className="text-brand p-4 text-center">
                                Searching...
                            </div>
                        )}
                        {playerSearch.length >= 2 &&
                            !searchLoading &&
                            searchData?.searchPlayers.length === 0 && (
                                <div className="text-brand p-4 text-center">
                                    No players found
                                </div>
                            )}
                        {playerSearch.length >= 2 &&
                            !searchLoading &&
                            searchData?.searchPlayers.map((player) => (
                                <button
                                    key={player.id}
                                    onClick={() => {
                                        navigate(
                                            `/dashboard/players/${player.id}`,
                                        );
                                        setPlayerSearch("");
                                        setShowMobileSearch(false);
                                    }}
                                    className="border-brand hover:bg-brand/10 w-full border-b p-4 text-left"
                                >
                                    <span className="text-brand text-base font-semibold">
                                        {player.name}
                                    </span>
                                    {player.nickname && (
                                        <span className="text-brand/60 ml-2 text-base">
                                            ({player.nickname})
                                        </span>
                                    )}
                                </button>
                            ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default TopNav;
